import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IContextualMenuProps } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { useTheme } from "@fluentui/react";

import { UILocation } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import type { MenuDefinition } from "@1js/acui-menu";
import {
  convertMenuDefinitionToFabricMenuProps,
  emptyMenuProps,
  useV9MenuEnabled,
} from "@1js/acui-menu";

import type { AppFlyoutAnchorProps } from "./AppFlyoutAnchor.types";
import type { AppSplitButtonProps } from "../../AppSplitButton/next";
import { ribbonExperiments } from "@1js/acui-experiments";

type MenuProps = AppFlyoutAnchorProps | AppSplitButtonProps;

// Type guard to differentiate between `AppFlyoutAnchorProps` and `AppSplitButtonProps`
const isAppFlyoutAnchorProps = (
  props: MenuProps
): props is AppFlyoutAnchorProps => {
  return "enableDownArrowNavigation" in props;
};

export const useGetMenuProps = (
  props: MenuProps,
  flyoutDropdownId: string,
  onMenuOpened: () => void,
  onSubMenuReady: (
    menu: MenuDefinition | undefined,
    flyoutMenuId: string | undefined
  ) => void,
  onCalloutReady: (menu: MenuDefinition | undefined) => void,
  onMenuDismissed: () => void
): ((menuDefinition: MenuDefinition | undefined) => IContextualMenuProps) => {
  const {
    controlInMenuRendererFunction,
    label,
    location,
    directionalHint,
    directionalHintFixed,
    useTargetAsMinWidth,
    calloutWidth,
    menuHostId,
    menuClassNames,
    menuItemStyles,
    preventCalloutDismissOnScroll,
    terminalParentControlId,
    id,
  } = props;

  const enableDownArrowNavigation = isAppFlyoutAnchorProps(props)
    ? props.enableDownArrowNavigation
    : undefined;

  const context = React.useContext(AppContext);
  const theme = useTheme();

  const isV9MenuEnabled = useV9MenuEnabled();

  // Method outputs a function to generate full menu props from menuDefinition. In case of deferred menus, the output can be passed into the menu
  // so that it can generate full menu props using menu definition it fetches from the store.
  const getMenuProps = React.useCallback(
    (menuDefinition: MenuDefinition | undefined) => {
      const baseMenuProps = menuDefinition
        ? convertMenuDefinitionToFabricMenuProps(
            {
              menuDefinition,
              controlInMenuRendererFunction,
              location: location as UILocation,
              onSubMenuPositioned: onSubMenuReady,
              customMenuClassNames: menuClassNames,
              customMenuItemStyles: menuItemStyles,
              arePerfImprovementsEnabled:
                context.isExperimentalFeatureEnabled?.(
                  "ContextualUIPerfImprovementsIsEnabled"
                ),
              onMenuPositioned: onCalloutReady,
              theme,
              terminalParentControlId: getTerminalParentControlId(
                id,
                location,
                terminalParentControlId
              ),
              useFullDescriptionLabelOnly:
                context.isExperimentalFeatureEnabled?.(
                  ribbonExperiments.useFullDescriptionLabelOnly
                ),
              isExperimentalFeatureEnabled:
                context.isExperimentalFeatureEnabled,
            },
            directionalHintFixed,
            context.appWindow,
            context.responsiveClassName,
            isV9MenuEnabled
          )
        : emptyMenuProps;

      return {
        ...baseMenuProps,
        onMenuOpened,
        onMenuDismissed,
        directionalHint,
        useTargetAsMinWidth,
        ariaLabel: label,
        id: flyoutDropdownId,
        calloutProps: {
          ...baseMenuProps.calloutProps,
          layerProps: { hostId: menuHostId },
          calloutWidth,
          preventDismissOnScroll: preventCalloutDismissOnScroll,
        },
        focusZoneProps: {
          ...baseMenuProps.focusZoneProps,
          shouldInputLoseFocusOnArrowKey: () =>
            enableDownArrowNavigation ?? false,
        },
      };
    },
    [
      context,
      controlInMenuRendererFunction,
      location,
      onSubMenuReady,
      menuClassNames,
      menuItemStyles,
      onCalloutReady,
      onMenuOpened,
      onMenuDismissed,
      directionalHint,
      directionalHintFixed,
      useTargetAsMinWidth,
      label,
      flyoutDropdownId,
      menuHostId,
      calloutWidth,
      preventCalloutDismissOnScroll,
      id,
      terminalParentControlId,
      theme,
      isV9MenuEnabled,
      enableDownArrowNavigation,
    ]
  );

  return getMenuProps;
};

// Returns the control ID of the top-most parent flyout that isn't an overflow. Return logic must be 1-1 mapping with the function in AppFlyoutAnchor (until v9 buttons ship).
// Don't set terminal parentControlId with the top-most control if that control would be an overflow.
const getTerminalParentControlId = (
  id: string,
  location?: string,
  terminalParentControlId?: string
) => {
  return location !== UILocation.Overflow
    ? (terminalParentControlId ?? id)
    : terminalParentControlId;
};
